import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { verticalListSortingStrategy } from '@dnd-kit/sortable'
import {
  ChevronRight,
  Copy,
  Database,
  Download,
  GripVertical,
  Lock,
  MoreHorizontal,
  PanelsTopLeft,
  Pencil,
  RefreshCw,
  Table2,
  Trash2,
  Webhook,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Application, DuplicateApplicationJob, Table } from '@/api/types'
import { ApplicationActions } from '@/features/application/components/application-actions'
import { useApplications, useOrderApplications } from '@/features/application/queries'
import { duplicateJobFor } from '@/features/job/duplicate'
import { isJobFailed } from '@/features/job/polling'
import { useJobs } from '@/features/job/queries'
import { WebhooksDialog } from '@/features/database/webhooks/webhooks-dialog'
import { TableAccessDialog } from '@/features/database/access/access-dialog'
import { DataSyncDialog } from '@/features/database/data-sync/data-sync-dialog'
import { ExportDialog } from '@/features/database/export/export-dialog'
import {
  useDeleteTable,
  useDuplicateTable,
  useUpdateTable,
} from '@/features/database/table/table-mutations'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'
import { NameDialog } from '@/shared/components/name-dialog'
import { SortableList } from '@/shared/components/sortable'
import { useSortableItem } from '@/shared/hooks/use-sortable-item'
import { byOrder } from '@/shared/lib/order'
import { Progress } from '@/shared/ui/progress'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/ui/collapsible'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/shared/ui/sidebar'

/**
 * One workspace's applications.
 *
 * Takes the id as a prop rather than resolving the selected workspace itself.
 * Which workspace is on screen is a question the shell answers once, from the url
 * and the session; a component that asked it again would be free to disagree, and
 * the version rendered under the workspace's own name is the one that must not.
 */
export function ApplicationList({ workspaceId }: { workspaceId: number }) {
  const { t } = useTranslation()
  const { data, isPending } = useApplications(workspaceId)
  const order = useOrderApplications()

  if (isPending) {
    return (
      <Group>
        <SidebarMenuItem>
          <SidebarMenuSkeleton showIcon />
        </SidebarMenuItem>
      </Group>
    )
  }

  const applications = byOrder(data ?? [])
  if (applications.length === 0) {
    return (
      <Group>
        <SidebarMenuItem>
          <p className="px-4 py-1.5 text-xs text-sidebar-foreground/60">
            {t('application.empty')}
          </p>
        </SidebarMenuItem>
      </Group>
    )
  }

  return (
    <SortableList
      ids={applications.map((application) => application.id)}
      strategy={verticalListSortingStrategy}
      // A sidebar row has nowhere useful to go sideways, and a drag that drifts
      // off the list reads as a drop target that is about to appear.
      modifiers={[restrictToVerticalAxis]}
      // The whole sequence, because that is what the endpoint takes: `order` is
      // one list per workspace across both application types, not a number each
      // item carries independently.
      onReorder={(applicationIds) => order.mutate({ workspaceId, applicationIds })}
    >
      <Group>
        {applications.map((application) => (
          <ApplicationItem
            key={application.id}
            workspaceId={workspaceId}
            application={application}
          />
        ))}
      </Group>
    </SortableList>
  )
}

function Group({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()

  return (
    <SidebarGroup>
      <SidebarGroupLabel>{t('application.group')}</SidebarGroupLabel>
      <SidebarMenu>{children}</SidebarMenu>
    </SidebarGroup>
  )
}

/**
 * When this tab was opened.
 *
 * A duplicate that fails is worth showing; one that failed last Tuesday is not,
 * and the job list is the user's recent jobs rather than this session's. This is
 * the line between the two.
 */
const OPENED_AT = Date.now()

function ApplicationItem({
  workspaceId,
  application,
}: {
  workspaceId: number
  application: Application
}) {
  const { t } = useTranslation()
  const sortable = useSortableItem(application.id)
  const { data: jobs } = useJobs()

  const job = duplicateJobFor(jobs ?? [], application.id, OPENED_AT)
  if (job !== undefined) {
    return <DuplicatingItem name={application.name} job={job} />
  }

  // Closed to this account. Drawn as a row rather than left out, so it is
  // visible that something is there -- hiding it is a separate, deliberate
  // setting. Nothing is offered on it: every operation below a closed
  // application is refused, reordering included, so a handle or a menu here
  // would only produce failed requests.
  if (!application.user_can_access) {
    return (
      <ClosedItem
        name={application.name}
        icon={application.type === 'dashboard' ? <PanelsTopLeft /> : <Database />}
      />
    )
  }

  const handle = (
    <SidebarMenuAction
      showOnHover
      className="right-7 cursor-grab"
      aria-label={t('application.reorder', { name: application.name })}
      {...sortable.handleProps}
    >
      <GripVertical />
    </SidebarMenuAction>
  )
  const actions = (
    <ApplicationActions workspaceId={workspaceId} application={application} />
  )

  if (application.type === 'database') {
    return (
      <DatabaseItem application={application} sortable={sortable}>
        {handle}
        {actions}
      </DatabaseItem>
    )
  }

  if (application.type === 'dashboard') {
    return (
      <SidebarMenuItem
        ref={sortable.setNodeRef}
        style={sortable.style}
        data-dragging={sortable.isDragging}
      >
        <SidebarMenuButton asChild tooltip={application.name}>
          <Link to="/dashboard/$dashboardId" params={{ dashboardId: application.id }}>
            <PanelsTopLeft />
            <span className="truncate">{application.name}</span>
          </Link>
        </SidebarMenuButton>
        {handle}
        {actions}
      </SidebarMenuItem>
    )
  }

  // An application type this build does not know. M8 deleted the builder and
  // automation ones, and a `core_application` row for either can outlive its
  // Django app -- production held a builder row until the drop migration. Drawn
  // as nothing rather than left to fall through, so such a row costs the sidebar
  // an entry and not a crash.
  return null
}

/**
 * An application this account cannot open, in place of the row that would
 * otherwise be there.
 *
 * Not a link and not a button: there is nothing behind it to show, and a
 * control that looked like one would be one.
 */
function ClosedItem({ name, icon }: { name: string; icon: React.ReactNode }) {
  const { t } = useTranslation()

  return (
    <SidebarMenuItem>
      <div
        className="flex h-10 items-center gap-4 rounded-md px-4 text-sm text-sidebar-foreground/50"
        title={t('applicationAccess.closed', { name })}
      >
        {icon}
        <span className="truncate">{name}</span>
        <Lock className="ml-auto size-3.5 shrink-0" />
      </div>
    </SidebarMenuItem>
  )
}

/**
 * An application being copied, in place of the row that would otherwise be there.
 *
 * In place rather than beside it: the copy does not exist yet, so there is
 * nothing to open, and a row that still looked like a link would be one.
 */
function DuplicatingItem({ name, job }: { name: string; job: DuplicateApplicationJob }) {
  const { t } = useTranslation()
  const failed = isJobFailed(job)

  return (
    <SidebarMenuItem className="px-4 py-1.5">
      <p className="truncate text-xs text-sidebar-foreground/70">
        {failed ? t('job.duplicateFailed', { name }) : t('job.duplicating', { name })}
      </p>
      {failed ? (
        <p className="mt-1 truncate text-xs text-destructive">
          {job.human_readable_error}
        </p>
      ) : (
        <Progress value={job.progress_percentage} className="mt-1.5 h-1" />
      )}
    </SidebarMenuItem>
  )
}

function DatabaseItem({
  application,
  sortable,
  children,
}: {
  application: Extract<Application, { type: 'database' }>
  sortable: ReturnType<typeof useSortableItem>
  children: React.ReactNode
}) {
  const { t } = useTranslation()

  return (
    <Collapsible asChild className="group/database">
      <SidebarMenuItem
        ref={sortable.setNodeRef}
        style={sortable.style}
        data-dragging={sortable.isDragging}
      >
        <CollapsibleTrigger asChild>
          <SidebarMenuButton tooltip={application.name}>
            <Database />
            <span className="truncate">{application.name}</span>
            {/* The grip and the actions menu are absolutely positioned over the
                row's right edge, so the chevron is held clear of both. At icon
                width there is room for one icon and the database's own is it. */}
            <ChevronRight className="mr-5 ml-auto transition-transform group-data-[state=open]/database:rotate-90 group-data-[collapsible=icon]:hidden" />
          </SidebarMenuButton>
        </CollapsibleTrigger>

        {children}

        <CollapsibleContent>
          <SidebarMenuSub>
            {application.tables.length === 0 ? (
              <li className="px-2 py-1 text-xs text-sidebar-foreground/60">
                {t('table.empty')}
              </li>
            ) : (
              byOrder(application.tables).map((table) => (
                <SidebarMenuSubItem key={table.id} className="group/table">
                  <SidebarMenuSubButton asChild>
                    <Link
                      to="/database/$databaseId/table/$tableId"
                      params={{ databaseId: application.id, tableId: table.id }}
                    >
                      <Table2 />
                      <span className="truncate">{table.name}</span>
                    </Link>
                  </SidebarMenuSubButton>
                  <TableActions
                    workspaceId={application.workspace.id}
                    databaseId={application.id}
                    table={table}
                    mayGovern={application.user_can_configure_access}
                  />
                </SidebarMenuSubItem>
              ))
            )}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}

/**
 * What can be done to a table rather than to a view.
 *
 * Webhooks belong to the table -- they fire for its rows whichever view
 * somebody is looking at -- so putting them in the view menu next door would be
 * filing them under the wrong thing.
 *
 * There is no *creating* a table here, and no importing a file into one. That
 * is not an omission: tables come from the API and the MCP tools in this fork,
 * and neither has ever had an interface path.
 */
function TableActions({
  workspaceId,
  databaseId,
  table,
  mayGovern,
}: {
  workspaceId: number
  databaseId: number
  table: Table
  mayGovern: boolean
}) {
  const { t } = useTranslation()
  const [dialog, setDialog] = useState<
    'webhooks' | 'rename' | 'delete' | 'export' | 'access' | 'sync' | null
  >(null)

  const rename = useUpdateTable()
  const remove = useDeleteTable()
  const duplicate = useDuplicateTable()

  const target = { workspaceId, databaseId, tableId: table.id }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuAction
            aria-label={t('table.actions', { name: table.name })}
            showOnHover
          >
            <MoreHorizontal />
          </SidebarMenuAction>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="right">
          <DropdownMenuItem onSelect={() => setDialog('rename')}>
            <Pencil />
            {t('action.rename')}
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={duplicate.isPending}
            onSelect={() => duplicate.mutate(table.id)}
          >
            <Copy />
            {t('action.duplicate')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setDialog('export')}>
            <Download />
            {t('export.title')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setDialog('webhooks')}>
            <Webhook className="size-4" />
            {t('webhook.title')}
          </DropdownMenuItem>
          {/*
            Only for a table that has one. Most do not, and an entry that opened
            a dialog saying "this is not synced" would be a menu item for a
            state rather than for an action.
          */}
          {table.data_sync != null && (
            <DropdownMenuItem onSelect={() => setDialog('sync')}>
              <RefreshCw />
              {t('dataSync.menu')}
            </DropdownMenuItem>
          )}
          {/*
            Offered to the database's owner and to the workspace admins, because
            the endpoint answers only to them in both directions. A control that
            403s reads as a bug rather than as a rule.
          */}
          {mayGovern && (
            <DropdownMenuItem onSelect={() => setDialog('access')}>
              <Lock />
              {t('access.menu')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem variant="destructive" onSelect={() => setDialog('delete')}>
            <Trash2 />
            {t('action.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {dialog === 'webhooks' && (
        <WebhooksDialog tableId={table.id} onClose={() => setDialog(null)} />
      )}

      {/* No view: the dialog offers the whole table, which is what this menu
          means. Exporting one view is offered on the view bar. */}
      {dialog === 'export' && (
        <ExportDialog
          tableId={table.id}
          view={undefined}
          onClose={() => setDialog(null)}
        />
      )}

      {dialog === 'sync' && table.data_sync != null && (
        <DataSyncDialog
          dataSyncId={table.data_sync.id}
          tableId={table.id}
          tableName={table.name}
          onClose={() => setDialog(null)}
        />
      )}

      {dialog === 'access' && (
        <TableAccessDialog
          workspaceId={workspaceId}
          tableId={table.id}
          tableName={table.name}
          onClose={() => setDialog(null)}
        />
      )}

      {dialog === 'rename' && (
        <NameDialog
          open
          onOpenChange={() => setDialog(null)}
          title={t('table.renameTitle')}
          label={t('application.name')}
          currentName={table.name}
          pending={rename.isPending}
          onSubmit={(name) => {
            rename.mutate({ ...target, name })
            setDialog(null)
          }}
        />
      )}

      {dialog === 'delete' && (
        <ConfirmDialog
          open
          onOpenChange={() => setDialog(null)}
          title={t('table.deleteTitle', { name: table.name })}
          description={t('table.deleteDescription')}
          confirmLabel={t('action.delete')}
          destructive
          onConfirm={() => {
            remove.mutate(target)
            setDialog(null)
          }}
        />
      )}
    </>
  )
}
